import React, { useEffect, useMemo, useState } from 'react';
import { MdCheck, MdKeyboardArrowDown, MdOutlineChatBubbleOutline } from 'react-icons/md';
import FillAppField from 'components/FillAppField';
import RepeatingGroup from 'components/RepeatingGroup';
import {
    type FillAppField as FieldDef,
    type FillAppSubtab as SubtabDef,
    type FillAppFieldGroup,
    type ChoiceGroups,
    type FillAppValues,
    fieldIsVisible,
    subtabGroups,
    groupFieldsOf,
    groupState,
    groupSummary,
    groupCommentCount,
} from 'global/fillapp';

import './index.css';

type Props = {
    subtab: SubtabDef;
    values: FillAppValues;
    cycleYear?: number;       // application cycle, for cycle-dependent widgets
    choiceGroups: ChoiceGroups;
    readOnly?: boolean;
    onChange?: (qid: string, value: unknown) => void;
    onBlur?: (qid: string) => void;
    // Open (unresolved) tutor-comment counts per qid — drives the card badges.
    openCommentsByQid?: Record<string, number>;
    // "Review the first note →": expand the card containing this qid and scroll
    // to it. tick re-triggers for repeated clicks on the same qid.
    focus?: { qid: string; tick: number } | null;
};

// Summary-first cards: each schema group renders as a collapsible card; one
// card is open at a time (accordion). Completed cards collapse to a one-line
// summary of their answers.
function FillAppSubtab({ subtab, values, cycleYear, choiceGroups, readOnly, onChange, onBlur, openCommentsByQid, focus }: Props) {
    const cards = subtabGroups(subtab);

    const rgroups = subtab.repeatingGroups ?? [];
    const fieldsByQid: Record<string, FieldDef> = {};
    subtab.fields.forEach(f => { fieldsByQid[f.qid] = f; });

    // Map each repeating-group qid to its group, and pick the anchor (first
    // member in field order) where the whole RepeatingGroup renders.
    const groupByQid = new Map<string, string>();
    const groupAnchor = new Map<string, string>();
    const groupById = new Map(rgroups.map(g => [g.id, g]));
    rgroups.forEach(g => {
        const members = [...g.sharedQids, ...g.slots.flat(), ...g.trailingSharedQids];
        members.forEach(q => groupByQid.set(q, g.id));
        const anchor = subtab.fields.find(f => members.includes(f.qid));
        if (anchor) groupAnchor.set(g.id, anchor.qid);
    });

    // Accordion: default-open card = the first one that isn't complete.
    const firstOpenId = useMemo(() => {
        const open = cards.find(g => groupState(subtab, g, values) !== 'complete');
        return (open ?? cards[0])?.id ?? null;
        // Recompute only when the subtab changes — not on every keystroke,
        // which would yank cards shut as the student finishes them.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subtab.id]);
    const [expandedId, setExpandedId] = useState<string | null>(firstOpenId);
    useEffect(() => { setExpandedId(firstOpenId); }, [firstOpenId]);

    // External focus (tutor-feedback wayfinding): open the card with the qid.
    useEffect(() => {
        if (!focus) return;
        const target = cards.find(g => g.qids.includes(focus.qid));
        if (!target) return;
        setExpandedId(target.id);
        // After the card expands, bring it into view.
        setTimeout(() => {
            document.getElementById(`fgcard-${target.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focus?.qid, focus?.tick]);

    const advanceFrom = (id: string) => {
        onBlur?.('');
        const idx = cards.findIndex(g => g.id === id);
        const next = cards[idx + 1];
        setExpandedId(next ? next.id : null);
        if (next) {
            setTimeout(() => {
                document.getElementById(`fgcard-${next.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 60);
        }
    };

    const renderFields = (group: FillAppFieldGroup) => {
        const renderedGroups = new Set<string>();
        return groupFieldsOf(subtab, group).map(field => {
            const gid = groupByQid.get(field.qid);
            if (gid) {
                if (groupAnchor.get(gid) !== field.qid || renderedGroups.has(gid)) return null;
                renderedGroups.add(gid);
                return (
                    <RepeatingGroup
                        key={`group-${gid}`}
                        group={groupById.get(gid)!}
                        fieldsByQid={fieldsByQid}
                        values={values}
                        cycleYear={cycleYear}
                        choiceGroups={choiceGroups}
                        readOnly={readOnly}
                        onChange={onChange}
                        onBlur={onBlur}
                    />
                );
            }
            if (!fieldIsVisible(field, values)) return null;
            return (
                <FillAppField
                    key={field.qid}
                    field={field}
                    value={values[field.qid]}
                    values={values}
                    cycleYear={cycleYear}
                    choiceGroups={choiceGroups}
                    readOnly={readOnly}
                    onChange={onChange}
                    onBlur={onBlur}
                />
            );
        });
    };

    return (
        <div className='fsubtab'>
            {cards.map(group => {
                // A card whose fields are all hidden right now renders nothing.
                const anyVisible = groupFieldsOf(subtab, group).some(f =>
                    groupByQid.has(f.qid) || fieldIsVisible(f, values));
                if (!anyVisible) return null;

                const state = groupState(subtab, group, values);
                const expanded = group.id === expandedId;
                const summary = expanded ? '' : groupSummary(subtab, group, values, choiceGroups);
                const commentCount = openCommentsByQid ? groupCommentCount(group, openCommentsByQid) : 0;
                const isLast = cards[cards.length - 1]?.id === group.id;

                return (
                    <div
                        key={group.id}
                        id={`fgcard-${group.id}`}
                        className={`fgcard ${expanded ? 'fgcard--open' : ''}`}
                    >
                        <button
                            type='button'
                            className='fgcard__head'
                            onClick={() => setExpandedId(expanded ? null : group.id)}
                        >
                            <span className={`fgcard__state fgcard__state--${state}`}>
                                {state === 'complete' && <MdCheck size={13} />}
                            </span>
                            <span className='fgcard__headMain'>
                                <span className='fgcard__title'>{group.title}</span>
                                {!expanded && (
                                    summary
                                        ? <span className='fgcard__summary'>{summary}</span>
                                        : <span className='fgcard__summary fgcard__summary--empty'>Not started</span>
                                )}
                            </span>
                            {commentCount > 0 && (
                                <span className='fgcard__cmt'>
                                    <MdOutlineChatBubbleOutline size={11} /> {commentCount}
                                </span>
                            )}
                            <span className='fgcard__affordance'>
                                <MdKeyboardArrowDown size={19} className={`fgcard__chev ${expanded ? 'fgcard__chev--up' : ''}`} />
                            </span>
                        </button>
                        {expanded && (
                            <div className='fgcard__body'>
                                {renderFields(group)}
                                {!readOnly && (
                                    <div className='fgcard__foot'>
                                        <button
                                            type='button'
                                            className='fgcard__continue'
                                            onClick={() => advanceFrom(group.id)}
                                        >
                                            {isLast ? 'Done with this section' : 'Save & continue'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default FillAppSubtab;
